<template>
  <div>
    <v-container fluid>
      <v-row dense class="pb-1">
        <v-col cols="3">
          <date-range :from.sync="from" :to.sync="to" v-model="dateDisplay" @change="refresh()" option="Last 30 days" hide-details/>
        </v-col>
        <v-col cols="3">
          <v-autocomplete label="Activity" dense outlined :items="activityTypes" v-model="activityType" hide-details @change="refresh()"/>
        </v-col>
        <v-col cols="3" v-if="sites.length>1">
          <v-autocomplete label="Site" dense outlined hide-details :items="[{id:null,name:'All'},{divider:true},...sites]" v-model="siteId" item-text="name" item-value="id" @change="refresh()"/>
        </v-col>
        <v-col cols="2">
          <v-autocomplete label="Show" :items="['Volume','Value']" outlined dense hide-details v-model="show"/>
        </v-col>
        <v-col class="text-right pr-2">
          <v-btn icon @click="printHtml('Non-excise alcohol activity', report())"><v-icon>mdi-printer</v-icon></v-btn>
          <v-tooltip bottom open-delay="1000" v-if="company.allowCSVExport">
            <template v-slot:activator="{on}">
              <v-btn icon @click="exportCSV" v-on="on"><v-icon>mdi-download</v-icon></v-btn>
            </template>
            Export as CSV file
          </v-tooltip>
        </v-col>
      </v-row>
    </v-container>

    <v-card class="mx-4 pa-4 overflow-y" height="calc(100vh - 184px)">
      <span v-html="report()"/>
    </v-card>

  </div>
</template>

<script>
import fileSaver from 'file-saver';
import printHtml from '@/mixins/printHtml'

export default {
  name: 'ReportsNonExciseActivity',
  mixins: [printHtml],
  data() {
    return {
      from: null,
      to: null,
      dateDisplay: '',
      siteId: null,
      show: 'Volume',
      loading: true,
      initialValue: 0,
      totalValue: 0,
      transactions: [],
      activityTypes: [{text:'All',value:null},{divider:true},{text:'Initial',value:'ExciseVesselTransactionInitial'},{text:'Adjust inventory',value:'ExciseVesselTransactionAdjustInventory'},
                      {text:'Mash',value:'Mash'},{text:'Distillation',value:'Distillation'},{text:'Bottling',value:'Bottling'},{text:'Blend',value:'Blend'},
                      {text:'Transfer site',value:'ExciseVesselTransactionTransferToSite'}
                      ],
      activityType: null
    }
  },
  computed: {
    company() {
      return this.$store.getters.company;
    },
    sites() {
      return this.$store.getters.sites(true);
    }
  },
  async created() {
    this.$emit('name', 'Non-excise activity')
    if (this.$route.query.from && this.$route.query.to) {
      this.from = this.$route.query.from;
      this.to = this.$route.query.to;
    }
  },
  methods: {
    async refresh() {
      this.loading = true;
      const results = await this.$apiCall(`query ReportsNonExciseActivity($from:Date!,$to:Date!,$siteId:ID, $refType:ObjectType) {
                                                nonExcisesValueBalanceTo(date:$from,siteId:$siteId)
                                                nonExcisesActivity(from:$from,to:$to,siteId:$siteId,refType:$refType) {
                                                  date,description,volume,alcoholName,value
                                                }
                                              }`,
                                          {from:this.from,to:this.to,siteId:this.siteId,refType:this.activityType});
      if (!results.error) {
        this.initialValue = results.data.nonExcisesValueBalanceTo;
        this.transactions = results.data.nonExcisesActivity;

        let balance = this.initialValue;
        this.totalValue = 0;
        for (const transaction of this.transactions) {
          this.total += transaction.value;
          balance += transaction.value;
          transaction.balance = balance;
        }
      }
      this.loading = false;
    },

    report() {
      const lines = [
                  `<style>
                    @page {size:landscape}
                    .reports-non-excise-activity {font-family: Roboto, sans-serif; width:100%;border-spacing:0;font-size:12px}
                    .reports-non-excise-activity th {height:36px;padding:0 16px;vertical-align:middle}
                    .reports-non-excise-activity .spacer {height:18px}
                    .reports-non-excise-activity td {height:36px;padding:0 16px;vertical-align:middle}
                    .reports-non-excise-activity .title {font-size:18px;text-transform:uppercase}
                    .reports-non-excise-activity .sup-title {font-weight:normal;font-size:22px}
                    .reports-non-excise-activity .sub-title {font-weight:normal;height:inherit;padding-bottom:20px;font-size:14px}
                    .reports-non-excise-activity .header th {border-bottom:1px solid #000;margin-bottom:20px}
                    .reports-non-excise-activity .text-left {text-align:left}
                    .reports-non-excise-activity .text-right {text-align:right}
                    .reports-non-excise-activity .font-weight-bold {font-weight:bold}
                    .reports-non-excise-activity .total-row {font-weight:bold}
                    .reports-non-excise-activity .capitalize {text-transform: capitalize}
                  </style>
                   <table class="reports-non-excise-activity">
                     <thead>
                        <tr>
                          <th colspan="6" class="sup-title">${this.company.name}</th>
                        </tr>
                        <tr>
                          <th colspan="6" class="title">Non-excise activity</th>
                        </tr>
                        <tr>
                           <th colspan="6" class="sub-title">${this.dateDisplay}</th>
                        </tr>
                        <tr class="header">
                          <th width="15%" class="text-left">Date</th>
                          <th width="40%" class="text-left">Activity</th>
                          <th width="30%" class="text-left">Non-excise</th>
                          <th width="15%" class="text-right">${this.show}</th>
                          ${this.show==='Value' ? '<th width="15%" class="text-right">Balance</th>' : ''}
                        </tr>
                      </thead>
                      <tbody>`
    ];
    for (const transaction of this.transactions) {
      lines.push(`<tr>`);
      lines.push(`<td>${this.$formatDate(transaction.date)}</td>`);
      lines.push(`<td>${transaction.description}</td>`);
      lines.push(`<td>${transaction.alcoholName}</td>`);
      if (this.show === 'Volume')
        lines.push(`<td class="text-right">${this.$number(transaction.volume,3)} litres</td>`);
      else {
        lines.push(`<td class="text-right">$${this.$number(transaction.value,2)}</td>`);
        lines.push(`<td class="text-right">$${this.$number(transaction.balance,2)}</td>`);
      }
      lines.push(`</tr>`);
      lines.push(`<tr><td colspan="5" class="spacer"/></tr>`);
    }
    lines.push(`</tbody>`);
    lines.push(`</table>`);
    return lines.join('');
    },

    exportCSV() {
      const lines = [ `"${this.company.name}",,,,`,
                      `"Non-excise activity",,,,`,
                      `"${this.dateDisplay}",,,,`,
                      `"Date","Activity","Non-excise","${this.show}","${this.show==='Value' ? 'Balance' :''}"`
                    ];
      for (const transaction of this.transactions)
        if (this.show === 'Volume')
          lines.push(`"${transaction.date}","${transaction.description}","${transaction.alcoholName}","${this.$number(transaction.volume)}",`);
        else
          lines.push(`"${transaction.date}","${transaction.description}","${transaction.alcoholName}","${this.$number(transaction.value,2)}","${this.$number(transaction.balance,2)}"`);

      // Write CSV file
      const blob = new Blob([lines.join('\r\n')], {type: "text/plain;charset=utf-8"});
      fileSaver.saveAs(blob, 'Non-excise activity.csv');
    }
  }
}
</script>